import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NavHeader from "../components/NavHeader";
import api from "../api/api";
import { errorProp } from "../helpers";

// ─── Stage Constants ───────────────────────────────────────────────────────────
const STAGE_REQUEST_OTP = "request_otp";
const STAGE_VERIFY_OTP = "verify_otp";
const STAGE_SET_PASSWORD = "set_password";

// ─── Root Page ─────────────────────────────────────────────────────────────────
const ResetPassword = () => {
  const [stage, setStage] = useState(STAGE_REQUEST_OTP);
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  return (
    <>
      <NavHeader />
      <div className="account-body">
        <div className="container">
          <div className="row vh-100">
            <div className="col-12 align-self-center">
              {stage === STAGE_REQUEST_OTP && (
                <RequestOtpComponent
                  email={email}
                  setEmail={setEmail}
                  onSuccess={() => setStage(STAGE_VERIFY_OTP)}
                />
              )}
              {stage === STAGE_VERIFY_OTP && (
                <VerifyOtpComponent
                  email={email}
                  onSuccess={(token) => {
                    setResetToken(token);
                    setStage(STAGE_SET_PASSWORD);
                  }}
                />
              )}
              {stage === STAGE_SET_PASSWORD && (
                <SetPasswordComponent email={email} resetToken={resetToken} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;

// ─── Stage 1: Request OTP ──────────────────────────────────────────────────────
const RequestOtpComponent = ({ email, setEmail, onSuccess }) => {
  const [btnText, setBtnText] = useState("Reset");
  const [loading, setLoading] = useState(false);
  const [alertClose, setAlertClose] = useState(false);

  const resetPassword = async (e) => {
    e.preventDefault();
    setBtnText("Loading...");
    setLoading(true);
    try {
      const { data } = await api.post(
        "/password/forgot-password/request-otp/",
        { email },
      );
      setAlertClose(true);
      setBtnText("Reset");
      toast.success(data?.success);
      setLoading(false);
      // Advance to OTP verification after a short delay so user sees the alert
      setTimeout(() => onSuccess(), 1500);
    } catch (error) {
      setBtnText("Failed, try again");
      setLoading(false);
      toast.error(error?.error, errorProp);
    }
  };

  return (
    <div className="auth-page">
      <div className="card auth-card shadow-lg">
        <div className="card-body">
          <div className="px-3">
            <div className="text-center auth-logo-text">
              <h4 className="mt-0 mb-3 mt-5">Forgot Password</h4>
              <p className="text-muted mb-0">
                Enter your email and an OTP will be sent to you.
              </p>
            </div>

            <form
              className="form-horizontal auth-form my-4"
              onSubmit={resetPassword}
            >
              <div className="form-group">
                {alertClose && (
                  <div className="alert dspg-info alert-dismissible fade show">
                    <h4 className="alert-heading">Check your email</h4>
                    <p>
                      We sent a one-time password (OTP) to your email address.
                    </p>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="alert"
                      aria-label="Close"
                      onClick={() => setAlertClose(false)}
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                )}
                <label htmlFor="email">Email</label>
                <div className="input-group mb-3">
                  <span className="auth-form-icon">
                    <i className="dripicons-mail"></i>
                  </span>
                  <input
                    id="email"
                    type="email"
                    className="form-control"
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group mb-0 row">
                <div className="col-12 mt-2">
                  <button
                    className="btn btn-gradient-primary btn-round btn-block waves-effect waves-light"
                    type="submit"
                    disabled={loading}
                  >
                    {btnText} <i className="fas fa-sign-in-alt ml-1"></i>
                  </button>
                </div>
              </div>
              <ToastContainer />
            </form>
          </div>

          <div className="m-3 text-center text-muted">
            <p>
              Remember it?{" "}
              <Link to="/login" className="text-primary ml-2">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Stage 2: Verify OTP ───────────────────────────────────────────────────────
const OTP_LENGTH = 6;

const VerifyOtpComponent = ({ email, onSuccess }) => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [btnText, setBtnText] = useState("Verify OTP");
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);

  const handleChange = (value, index) => {
    // Accept only single digit
    if (!/^\d?$/.test(value)) return;

    const next = [...otp];
    next[index] = value;
    setOtp(next);

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = [...otp];
    pasted.split("").forEach((char, i) => {
      next[i] = char;
    });
    setOtp(next);
    // Focus the next empty field or last field
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length < OTP_LENGTH) {
      toast.error("Please enter the complete OTP.", errorProp);
      return;
    }

    setBtnText("Verifying...");
    setLoading(true);
    try {
      const { data } = await api.post("/password/forgot-password/verify-otp/", {
        email,
        otp: otpValue,
      });
      toast.success(data?.success || "OTP verified successfully!");
      setLoading(false);
      // Pass reset_token up to the parent
      onSuccess(data?.reset_token);
    } catch (error) {
      setBtnText("Failed, try again");
      setLoading(false);
      toast.error(error?.error || "Invalid OTP. Please try again.", errorProp);
    }
  };

  return (
    <div className="auth-page">
      <div className="card auth-card shadow-lg">
        <div className="card-body">
          <div className="px-3">
            <div className="text-center auth-logo-text">
              <h4 className="mt-0 mb-3 mt-5">Verify OTP</h4>
              <p className="text-muted mb-0">
                Enter the {OTP_LENGTH}-digit code sent to{" "}
                <strong>{email}</strong>.
              </p>
            </div>

            <form className="form-horizontal my-4" onSubmit={verifyOtp}>
              {/* OTP Input Boxes */}
              <div className="form-group">
                <div
                  className="d-flex justify-content-between mb-4"
                  style={{ gap: "px" }}
                  onPaste={handlePaste}
                >
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputsRef.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="form-control text-center font-weight-bold"
                      style={{
                        width: "48px",
                        height: "52px",
                        fontSize: "1.4rem",
                        borderRadius: "8px",
                        flex: "1",
                      }}
                      required
                    />
                  ))}
                </div>
              </div>

              <div className="form-group mb-0 row">
                <div className="col-12 mt-2">
                  <button
                    className="btn btn-gradient-primary btn-round btn-block waves-effect waves-light"
                    type="submit"
                    disabled={loading}
                  >
                    {btnText} <i className="fas fa-check ml-1"></i>
                  </button>
                </div>
              </div>
              <ToastContainer />
            </form>
          </div>

          <div className="m-3 text-center text-muted">
            <p>
              Remember your password?{" "}
              <Link to="/login" className="text-primary ml-2">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Stage 3: Set New Password ─────────────────────────────────────────────────
const SetPasswordComponent = ({ email, resetToken }) => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirm] = useState("");
  const [btnText, setBtnText] = useState("Set Password");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const setPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.", errorProp);
      return;
    }

    setBtnText("Saving...");
    setLoading(true);
    try {
      const { data } = await api.post(
        "/password/forgot-password/set-password/",
        {
          email,
          reset_token: resetToken,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      );
      toast.success(data?.success || "Password updated successfully!");
      setLoading(false);
      setSuccess(true);
    } catch (error) {
      setBtnText("Failed, try again");
      setLoading(false);
      toast.error(
        error?.error || "Something went wrong. Please try again.",
        errorProp,
      );
    }
  };

  return (
    <div className="auth-page">
      <div className="card auth-card shadow-lg">
        <div className="card-body">
          <div className="px-3">
            <div className="text-center auth-logo-text">
              <h4 className="mt-0 mb-3 mt-5">Set New Password</h4>
              <p className="text-muted mb-0">
                Choose a strong new password for your account.
              </p>
            </div>

            {success ? (
              /* ── Success state ── */
              <div className="text-center my-4">
                <div
                  className="mb-3"
                  style={{ fontSize: "3rem", color: "#28a745" }}
                >
                  <i className="fas fa-check-circle"></i>
                </div>
                <h5 className="font-weight-bold">Password Updated!</h5>
                <p className="text-muted">
                  Your password has been reset successfully. You can now sign in
                  with your new password.
                </p>
                <button
                  className="btn btn-gradient-primary btn-round btn-block waves-effect waves-light mt-3"
                  onClick={() => navigate("/login")}
                >
                  Go to Login <i className="fas fa-sign-in-alt ml-1"></i>
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <form
                className="form-horizontal auth-form my-4"
                onSubmit={setPassword}
              >
                {/* Email – read-only */}
                <div className="form-group">
                  <label htmlFor="reset-email">Email</label>
                  <div className="input-group mb-3">
                    <span className="auth-form-icon">
                      <i className="dripicons-mail"></i>
                    </span>
                    <input
                      id="reset-email"
                      type="email"
                      className="form-control"
                      value={email}
                      readOnly
                      style={{
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                </div>

                {/* New Password */}
                <div className="form-group">
                  <label htmlFor="new-password">New Password</label>
                  <div className="input-group mb-3">
                    <input
                      id="new-password"
                      type={showNew ? "text" : "password"}
                      className="form-control"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowNew((p) => !p)}
                        tabIndex={-1}
                        style={{ borderRadius: "0 4px 4px 0" }}
                      >
                        <i
                          className={`fas ${showNew ? "fa-eye-slash" : "fa-eye"}`}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <div className="input-group mb-3">
                    <input
                      id="confirm-password"
                      type={showConfirm ? "text" : "password"}
                      className="form-control"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={8}
                    />
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowConfirm((p) => !p)}
                        tabIndex={-1}
                        style={{ borderRadius: "0 4px 4px 0" }}
                      >
                        <i
                          className={`fas ${showConfirm ? "fa-eye-slash" : "fa-eye"}`}
                        ></i>
                      </button>
                    </div>
                  </div>
                  {/* Inline mismatch hint */}
                  {confirmPassword && newPassword !== confirmPassword && (
                    <small className="text-danger">
                      Passwords do not match.
                    </small>
                  )}
                </div>

                <div className="form-group mb-0 row">
                  <div className="col-12 mt-2">
                    <button
                      className="btn btn-gradient-primary btn-round btn-block waves-effect waves-light"
                      type="submit"
                      disabled={loading}
                    >
                      {btnText} <i className="fas fa-save ml-1"></i>
                    </button>
                  </div>
                </div>
                <ToastContainer />
              </form>
            )}
          </div>

          {!success && (
            <div className="m-3 text-center text-muted">
              <p>
                Remember your password?{" "}
                <Link to="/login" className="text-primary ml-2">
                  Sign in here
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
